import LZString from 'lz-string';

const routes = [
  {
    path: '/',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        component: () => import('pages/IndexPage.vue'),
        props: route =>( {
          code: !!route.query.code ? route.query.code : undefined,
          shareButton: route.query.shareButton === 'true',
          prompt: !!route.query.prompt ? route.query.prompt : undefined,
        }),
      },
    ]
  },
  {
    path: '/studio',
    component: () => import('layouts/StudioLayout.vue'),
    children: [
      {
        path: '/studio',
        component: () => import('pages/FormStudio.vue'),
        props: route =>( {
          buttons: extractButtons(route.query),
          saveButton: route.query.saveButton === 'true',
          publishButton: route.query.publishButton === 'true',
          closeButton: route.query.closeButton === 'true',
          debug: route.query.debug === 'true',
          code: route.query.code ? LZString.decompressFromEncodedURIComponent(route.query.code) : undefined,
          prompt: !!route.query.prompt ? route.query.prompt : undefined,
          shareButton: route.query.shareButton === 'true',
          mediaButton: route.query.mediaButton === 'true',
        }),
      },
    ]
  },
  // Always leave this as last one,
  // but you can also remove it
  {
    path: '/:catchAll(.*)*',
    component: () => import('pages/ErrorNotFound.vue')
  }
]

export default routes


/**
 * Extracts and returns an ordered array of button definitions from query parameters.
 *
 * The function looks for parameters matching the pattern `btn{index}Label` and `btn{index}Name`,
 * where `{index}` is any positive integer. It pairs labels and names with matching indices
 * to form button objects. The result is sorted by index and excludes incomplete button definitions.
 *
 * @param {Object} queryParams - A map of query parameters (typically parsed from the URL).
 * @returns {Array<{label: string, name: string}>} An array of button objects sorted by index.
 *
 * @example
 * const queryParams = {
 *   btn2Name: 'publish',
 *   btn1Label: 'Save',
 *   btn1Name: 'save',
 *   btn2Label: 'Publish',
 *   otherParam: 'ignored'
 * };
 *
 * const buttons = extractButtons(queryParams);
 * // Result: [
 * //   { label: 'Save', name: 'save' },
 * //   { label: 'Publish', name: 'publish' }
 * // ]
 */
function extractButtons(queryParams) {
  const buttonsMap = new Map();

  for (const [key, value] of Object.entries(queryParams)) {
    const match = key.match(/^btn(\d+)(Label|Name)$/);
    if (!match) continue;

    const index = parseInt(match[1], 10);
    const type = match[2].toLowerCase(); // 'label' or 'name'

    if (!buttonsMap.has(index)) {
      buttonsMap.set(index, {});
    }

    buttonsMap.get(index)[type] = value;
  }

  // Convert the map to an array, sorted by index
  return [...buttonsMap.entries()]
    .sort((a, b) => a[0] - b[0]) // sort by index
    .map(([_, button]) => button)
    .filter(btn => btn.label && btn.name); // ensure both parts exist
}
